import { useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { authService } from '../services/auth'
import { User, Mail, Shield, Calendar, Edit2, Save, X, Lock } from 'lucide-react'

const ProfilePage = () => {
  const { profile, user } = useAuth()
  const [isEditing, setIsEditing] = useState(false)
  const [isChangingPassword, setIsChangingPassword] = useState(false)

  // Profile Form State
  const [formData, setFormData] = useState({
    full_name: profile?.full_name || '',
    email: user?.email || ''
  })

  // Password Form State
  const [passData, setPassData] = useState({
    password: '',
    confirmPassword: ''
  })

  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)

  if (!profile || !user) {
    return <div className="p-6">Carregando perfil...</div>
  }

  const formatarData = (data: string) => {
    return new Date(data).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    })
  }

  // Handle Profile Update
  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setMessage(null)

    try {
      // 1. Update Profile (Name)
      if (formData.full_name !== profile.full_name) {
        const { error } = await authService.updateProfile({ full_name: formData.full_name })
        if (error) throw new Error(error)
      }

      // 2. Update Email (if changed)
      if (formData.email !== user.email) {
        const { error } = await authService.updateEmail(formData.email)
        if (error) throw new Error(error)
        setMessage({ type: 'success', text: 'Perfil atualizado! Verifique seu novo email para confirmação.' })
      } else {
        setMessage({ type: 'success', text: 'Perfil atualizado com sucesso!' })
      }

      setIsEditing(false)
      // Note: Ideally refresh auth state here, but simple update might suffice if useAuth subscribes to changes
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Erro ao atualizar perfil' })
    } finally {
      setLoading(false)
    }
  }

  // Handle Password Update
  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    if (passData.password !== passData.confirmPassword) {
      setMessage({ type: 'error', text: 'As senhas não conferem' })
      return
    }

    setLoading(true)
    setMessage(null)

    try {
      const { error } = await authService.updatePassword(passData.password)
      if (error) throw new Error(error)

      setMessage({ type: 'success', text: 'Senha alterada com sucesso!' })
      setIsChangingPassword(false)
      setPassData({ password: '', confirmPassword: '' })
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Erro ao alterar senha' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Meu Perfil</h1>
        <p className="text-gray-600 mt-1">Gerencie suas informações pessoais</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border max-w-2xl">
        {/* Header */}
        <div className="p-6 border-b flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="h-20 w-20 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-2xl font-bold">
              {profile.full_name?.charAt(0) || 'U'}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{profile.full_name}</h2>
              <p className="text-gray-500 capitalize">{profile.role}</p>
            </div>
          </div>
          {!isEditing && !isChangingPassword && (
            <button
              onClick={() => {
                setFormData({ full_name: profile.full_name || '', email: user.email || '' })
                setIsEditing(true)
              }}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Editar Perfil"
            >
              <Edit2 className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Messages */}
        {message && (
          <div className={`mx-6 mt-6 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            {message.text}
          </div>
        )}

        <div className="p-6 space-y-6">
          {/* View Mode */}
          {!isEditing && !isChangingPassword && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Nome Completo</label>
                <div className="flex items-center gap-2 text-gray-900">
                  <User className="w-4 h-4 text-gray-400" />
                  {profile.full_name}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">E-mail</label>
                <div className="flex items-center gap-2 text-gray-900">
                  <Mail className="w-4 h-4 text-gray-400" />
                  {user.email}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Função</label>
                <div className="flex items-center gap-2 text-gray-900">
                  <Shield className="w-4 h-4 text-gray-400" />
                  <span className="capitalize">{profile.role}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Membro desde</label>
                <div className="flex items-center gap-2 text-gray-900">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  {formatarData(profile.created_at)}
                </div>
              </div>
            </div>
          )}

          {/* Edit Profile Mode */}
          {isEditing && (
            <form onSubmit={handleUpdateProfile} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome Completo</label>
                <div className="relative">
                  <User className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={e => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={loading} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  <Save className="w-4 h-4" /> Salvar
                </button>
                <button type="button" onClick={() => setIsEditing(false)} className="flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200">
                  <X className="w-4 h-4" /> Cancelar
                </button>
              </div>
            </form>
          )}

          {/* Change Password Mode */}
          {isChangingPassword && (
            <form onSubmit={handleChangePassword} className="space-y-4 border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900">Alterar Senha</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nova Senha</label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                  <input
                    type="password"
                    value={passData.password}
                    onChange={e => setPassData(prev => ({ ...prev, password: e.target.value }))}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                    minLength={6}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar Senha</label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                  <input
                    type="password"
                    value={passData.confirmPassword}
                    onChange={e => setPassData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                    minLength={6}
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={loading} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  <Save className="w-4 h-4" /> Atualizar Senha
                </button>
                <button type="button" onClick={() => setIsChangingPassword(false)} className="flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200">
                  <X className="w-4 h-4" /> Cancelar
                </button>
              </div>
            </form>
          )}

          {!isEditing && !isChangingPassword && (
            <div className="pt-6 border-t">
              <h3 className="text-md font-medium text-gray-900 mb-4">Segurança</h3>
              <button
                className="flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 text-sm font-medium transition-colors"
                onClick={() => setIsChangingPassword(true)}
              >
                <Lock className="w-4 h-4" />
                Alterar Senha
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ProfilePage
